import type { ChatLog } from '../../types/admin';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { User, Calendar, Globe, MessageSquare, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface ParticipantInfoPanelProps {
  conversation: ChatLog;
}

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'MMM d, yyyy h:mm:ss a');
  } catch {
    return dateStr;
  }
}

function calculateAge(dateOfBirth: string): number | undefined {
  if (!dateOfBirth) return undefined;

  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return undefined;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

function calculateDuration(startTime: string, endTime?: string): string {
  try {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Less than a minute';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;

    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  } catch {
    return 'Unknown';
  }
}

export default function ParticipantInfoPanel({
  conversation,
}: ParticipantInfoPanelProps) {
  const age = conversation.patientDateOfBirth
    ? calculateAge(conversation.patientDateOfBirth)
    : undefined;

  const userMessages = conversation.messages.filter((m) => m.role === 'user').length;
  const assistantMessages = conversation.messages.filter(
    (m) => m.role === 'assistant'
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center space-x-2">
          <User className="h-5 w-5" />
          <span>Participant Info</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
            Name
          </p>
          <p className="font-medium text-gray-900">
            {conversation.patientFirstName || conversation.patientLastName
              ? `${conversation.patientFirstName || ''} ${conversation.patientLastName || ''}`.trim()
              : 'Not provided'}
          </p>
        </div>

        {/* Date of Birth & Age */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
            Date of Birth
          </p>
          <p className="font-medium text-gray-900">
            {conversation.patientDateOfBirth || 'Not provided'}
            {age !== undefined && (
              <span className="text-gray-500 ml-2">({age} years old)</span>
            )}
          </p>
        </div>

        {/* Session ID */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
            Session ID
          </p>
          <p className="font-mono text-sm text-gray-600 break-all">
            {conversation.sessionId}
          </p>
        </div>

        {/* Patient ID */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
            Patient ID
          </p>
          <p className="font-mono text-sm text-gray-600 break-all">
            {conversation.patientId}
          </p>
        </div>

        <hr className="border-gray-200" />

        {/* Session Info */}
        <div className="flex items-center space-x-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-xs text-gray-500">Started</p>
            <p className="text-sm text-gray-900">
              {formatDate(conversation.startTime)}
            </p>
          </div>
        </div>

        {conversation.endTime && (
          <div className="flex items-center space-x-3">
            <Clock className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-sm text-gray-900">
                {calculateDuration(conversation.startTime, conversation.endTime)}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3">
          <MessageSquare className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-xs text-gray-500">Messages</p>
            <p className="text-sm text-gray-900">
              {conversation.messages.length} total ({userMessages} user,{' '}
              {assistantMessages} AI)
            </p>
          </div>
        </div>

        {/* Metadata */}
        {conversation.metadata && (
          <>
            <hr className="border-gray-200" />

            {conversation.metadata.appMode && (
              <div className="flex items-center space-x-3">
                <Globe className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">App Mode</p>
                  <p className="text-sm text-gray-900 capitalize">
                    {conversation.metadata.appMode}
                  </p>
                </div>
              </div>
            )}

            {conversation.metadata.userAgent && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                  User Agent
                </p>
                <p className="text-xs text-gray-600 break-all">
                  {conversation.metadata.userAgent}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
